import type { ApiCatalogListItem } from '@chatsdet/types';
import type { WorkflowNode } from '../../../types/workflow';

/**
 * Enriches api_sequence nodes with step display data derived from the API catalog.
 *
 * The node's `steps` array contains `apiRequestId` references, but the canvas display
 * needs `method` and `urlPattern` to show the API calls. This function looks up each
 * step's apiRequestId in the API catalog and adds the display fields.
 */
export function deriveApiSequenceStepLabels({
  nodes,
  apiRequests,
}: {
  nodes: WorkflowNode[];
  apiRequests: ApiCatalogListItem[];
}): WorkflowNode[] {
  if (nodes.length === 0) {
    return nodes;
  }

  // Build a lookup map for API requests by ID
  const apiRequestMap = new Map<string, ApiCatalogListItem>();
  for (const req of apiRequests) {
    apiRequestMap.set(req.id, req);
  }

  return nodes.map((node) => {
    if (node.type !== 'api_sequence') {
      return node;
    }

    const data = node.data as Record<string, unknown> | undefined;
    const steps = Array.isArray(data?.steps) ? (data.steps as Array<Record<string, unknown>>) : [];

    if (steps.length === 0) {
      return node;
    }

    // Enrich each step with method and urlPattern from API catalog
    const enrichedSteps = steps.map((step, index) => {
      const apiRequestId = typeof step.apiRequestId === 'string' ? step.apiRequestId : null;
      const apiRequest = apiRequestId ? apiRequestMap.get(apiRequestId) : null;
      const method = typeof step.method === 'string' && step.method ? step.method : null;
      const urlTemplate =
        typeof step.urlTemplate === 'string' && step.urlTemplate ? step.urlTemplate : null;

      return {
        stepId: step.stepId || `step${index + 1}`,
        // Use method override if set, otherwise use API catalog method
        method: method || apiRequest?.method || 'GET',
        // Use urlTemplate if set, otherwise use API pattern from catalog
        urlPattern: urlTemplate || apiRequest?.pattern || null,
        credentialName: null, // Could be enriched later if needed
      };
    });

    return {
      ...node,
      data: {
        ...data,
        _stepLabels: enrichedSteps,
      },
    };
  });
}

export function apiSequenceStepsNeedCatalog(nodes: WorkflowNode[]): boolean {
  return nodes.some((node) => {
    if (node.type !== 'api_sequence') return false;

    const steps = Array.isArray(node.data?.steps)
      ? (node.data.steps as Array<Record<string, unknown>>)
      : [];

    return steps.some((step) => {
      const hasApiRequestId =
        typeof step.apiRequestId === 'string' && step.apiRequestId.length > 0;
      const hasMethod = typeof step.method === 'string' && step.method.length > 0;
      const hasUrlTemplate =
        typeof step.urlTemplate === 'string' && step.urlTemplate.length > 0;

      return hasApiRequestId && (!hasMethod || !hasUrlTemplate);
    });
  });
}
